import { BaseComponent, ComponentConfig } from './base-component';
import { triggerEvent } from './util/event-handler';

const EVENT_EXPAND = 'antumo:sidebar:expand';
const EVENT_EXPANDED = 'antumo:sidebar:expanded';
const EVENT_COLLAPSE = 'antumo:sidebar:collapse';
const EVENT_COLLAPSED = 'antumo:sidebar:collapsed';

const CLASS_EXPANDED = 'navigation-expanded';
const CLASS_COLLAPSED = 'navigation-collapsed';
const CLASS_BODY_COLLAPSED = 'sidebar-collapsed';
const SELECTOR_TOGGLE = '.navigation-toggle';

export class Sidebar extends BaseComponent {
  static get NAME() { return 'sidebar'; }

  static DefaultConfig: ComponentConfig = {
    persist: false,
    storageKey: 'antumo-sidebar-state',
  };

  private _toggleButton: HTMLElement | null = null;

  constructor(element: Element, config: ComponentConfig = {}) {
    super(element, config);

    if (this._toggleButton !== null || this._element !== element) return;

    this._toggleButton = (this._element as HTMLElement).querySelector(SELECTOR_TOGGLE);
    this._restoreState();
    this._updateAria();
  }

  expand(): void {
    if (!this._element || this.isExpanded()) return;

    const event = triggerEvent(this._element, EVENT_EXPAND, {}, true);
    if (event.defaultPrevented) return;

    this._element.classList.remove(CLASS_COLLAPSED);
    this._element.classList.add(CLASS_EXPANDED);
    document.body.classList.remove(CLASS_BODY_COLLAPSED);
    this._updateAria();
    this._saveState('expanded');
    triggerEvent(this._element, EVENT_EXPANDED, {}, false);
  }

  collapse(): void {
    if (!this._element || this.isCollapsed()) return;

    const event = triggerEvent(this._element, EVENT_COLLAPSE, {}, true);
    if (event.defaultPrevented) return;

    this._element.classList.remove(CLASS_EXPANDED);
    this._element.classList.add(CLASS_COLLAPSED);
    document.body.classList.add(CLASS_BODY_COLLAPSED);
    this._updateAria();
    this._saveState('collapsed');
    triggerEvent(this._element, EVENT_COLLAPSED, {}, false);
  }

  toggle(): void {
    if (this.isExpanded()) {
      this.collapse();
    } else {
      this.expand();
    }
  }

  isExpanded(): boolean {
    return this._element?.classList.contains(CLASS_EXPANDED) ?? false;
  }

  isCollapsed(): boolean {
    return !this.isExpanded();
  }

  dispose(): void {
    this._toggleButton = null;
    super.dispose();
  }

  private _restoreState(): void {
    if (!this._config.persist) return;

    try {
      const saved = localStorage.getItem(this._config.storageKey as string);
      if (saved === 'collapsed') {
        this._element!.classList.remove(CLASS_EXPANDED);
        this._element!.classList.add(CLASS_COLLAPSED);
        document.body.classList.add(CLASS_BODY_COLLAPSED);
      } else if (saved === 'expanded') {
        this._element!.classList.remove(CLASS_COLLAPSED);
        this._element!.classList.add(CLASS_EXPANDED);
        document.body.classList.remove(CLASS_BODY_COLLAPSED);
      }
    } catch {
      // localStorage unavailable — degrade gracefully
    }
  }

  private _saveState(state: 'expanded' | 'collapsed'): void {
    if (!this._config.persist) return;

    try {
      localStorage.setItem(this._config.storageKey as string, state);
    } catch {
      // localStorage unavailable — degrade gracefully
    }
  }

  private _updateAria(): void {
    if (!this._toggleButton) return;
    this._toggleButton.setAttribute('aria-expanded', String(this.isExpanded()));
  }
}
